import { PlusIcon } from '@heroicons/react/24/solid'
import { ColumnDef } from '@tanstack/react-table'
import { CloudUpload } from 'iconoir-react'
import Button from 'app/components/Button'
import ImportWizard from 'app/components/ImportWizard'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Modal } from 'app/components/Modal'
import { PageHeader } from 'app/components/PageHeader'
import SideBarModal from 'app/components/SideBarModal'
import Step1 from 'app/components/steps/Step1'
import Step2 from 'app/components/steps/Step2'
import Step3 from 'app/components/steps/Step3'
import Step4 from 'app/components/steps/Step4'
import Step5 from 'app/components/steps/Step5'
import SimpleTable from 'app/components/table/SimpleTable'
import Spinner from 'app/components/Spinner'
import TableLayout from 'app/components/table/TableLayout'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { FilterableColumn } from 'app/filter.utils'
import { FileImportJson } from 'app/types/entities'
import { ImportJson } from 'app/types/entities'
import { fileImportColumns } from './fileImportColumns'

export type ImportsComponentProps = {
  fileImports: { hits: FileImportJson[]; total: number } | undefined
  isLoading: boolean
  customTotalItemsText: string
  columns: ColumnDef<any, any>[]
  filterableColumns: FilterableColumn[]
  isImportedFileOpen: boolean
  shouldDeleteFile: boolean
  selectedFile: FileImportJson | null
  importContent: ImportJson[] | undefined
  isDeleting: boolean
  openImportModal: boolean
  orgId: string | undefined
  handleDelete: (file: FileImportJson) => void
  closeFileModal: () => void
  setShouldDeleteFile: (val: boolean) => void
  setOpenImportModal: (val: boolean) => void
  setSearchValue: (val: string | undefined) => void
}

export function ImportsComponent({
  fileImports,
  isLoading,
  customTotalItemsText,
  columns,
  filterableColumns,
  isImportedFileOpen,
  shouldDeleteFile,
  selectedFile,
  importContent,
  isDeleting,
  openImportModal,
  orgId,
  handleDelete,
  closeFileModal,
  setShouldDeleteFile,
  setOpenImportModal,
  setSearchValue,
}: ImportsComponentProps): React.ReactElement {
  // Show loading spinner while fetching
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner size={10} />
      </div>
    )
  }

  return (
    <>
      <PageScroll
        variant="table"
        header={
          <PageHeader
            title="All Imports"
            totalItems={customTotalItemsText}
            primaryButtonSlot={
              <button className="btn btn-primary btn-md flex items-center" onClick={() => setOpenImportModal(true)}>
                <PlusIcon className="mr-2 h-5 w-5 stroke-current text-on-color" />
                <span>Import more activities</span>
              </button>
            }
          />
        }
      >
        <TableLayout
          filterableColumns={filterableColumns}
          data={fileImports}
          columns={columns}
          edgePadded
          emptyState={
            <TableEmptyState
              icon={<CloudUpload className="h-6 w-6" strokeWidth={1.5} />}
              title="No imports yet"
              description="Upload a CSV to bring activities into Trackable in bulk. Each import stays here so you can open it later and see what came in."
              action={
                <button className="btn btn-primary btn-sm flex items-center" onClick={() => setOpenImportModal(true)}>
                  <PlusIcon className="mr-1 h-4 w-4 stroke-current text-on-color" />
                  <span>Import more activities</span>
                </button>
              }
            />
          }
        />
        {/* eslint-disable-next-line deprecation/deprecation */}
        <Modal
          open={isImportedFileOpen && selectedFile !== null}
          title={selectedFile?.fileName}
          isFullScreen={false}
          titleClassName="normal-case"
          onClose={closeFileModal}
          is80Percent
          children={
            <div className="flex h-full flex-col overflow-hidden pb-2 pt-6">
              <SimpleTable<ImportJson>
                key={selectedFile?.id}
                data={importContent}
                columns={fileImportColumns(orgId)}
                showColumnOrdering={false}
                onSearchValueChange={(val) => setSearchValue(val)}
                hideFooter
              />
              <div className="sticky bottom-0 self-end px-6 pt-6">
                {shouldDeleteFile ? (
                  <div className="flex items-center gap-x-4 align-middle">
                    <span className="order-1 text-body-sm text-strong">
                      Are you sure you want to delete this import and all associated activities?
                    </span>
                    <Button
                      label="No, keep the import"
                      mode="white"
                      size="md"
                      disabled={isDeleting}
                      className="order-2 rounded-full border border-default !bg-surface-subtle px-6 py-3.5 text-body-sm  text-strong shadow-elevation-1"
                      onClick={() => setShouldDeleteFile(false)}
                    />
                    <Button
                      label={isDeleting ? `Delete in progress...` : `Yes, delete`}
                      mode="danger"
                      size="md"
                      disabled={isDeleting}
                      className="order-3 justify-center rounded-full border-red-400 !bg-surface px-6 py-3.5 text-red-500"
                      onClick={() => handleDelete(selectedFile!)}
                    />
                  </div>
                ) : importContent ? (
                  <Button
                    label="Delete import"
                    mode="danger"
                    size="md"
                    className="justify-center rounded-full border-red-400 !bg-surface px-6 py-3.5 text-red-500"
                    onClick={() => setShouldDeleteFile(true)}
                  />
                ) : null}
              </div>
            </div>
          }
        />
      </PageScroll>
      <SideBarModal open={openImportModal} onClose={() => false}>
        <div className="flex h-full flex-col pt-2">
          <ImportWizard title="Activities Import" totalSteps={5} onComplete={() => setOpenImportModal(false)}>
            <ImportWizard.Steps>
              <Step1 key="step-1" />
              <Step2 key="step-2" />
              <Step3 key="step-3" />
              <Step4 key="step-4" />
              <Step5 key="step-5" />
            </ImportWizard.Steps>
            <ImportWizard.Buttons />
          </ImportWizard>
        </div>
      </SideBarModal>
    </>
  )
}
